import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, ChevronRight, ExternalLink, FlaskConical, Newspaper, PlayCircle, RefreshCw, Youtube } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Empty, Loader, Pill } from "@/components/Bits";
import { PracticePanel } from "@/components/PracticePanel";

const TABS = [
  ["topics", "Topics"],
  ["practice", "Practice"],
  ["resources", "Other resources"],
  ["real_world", "Real world"],
  ["news", "News"],
];
const statusTone = { completed: "green", in_progress: "amber", not_started: "slate" };
const ENDPOINTS = { resources: "resources", real_world: "real-world", news: "news" };

const useAsset = (chapterId, kind, active) => {
  const [asset, setAsset] = useState(null);

  const load = useCallback(
    async (refresh = false) => {
      const { data } = await api.get(`/chapters/${chapterId}/${ENDPOINTS[kind]}${refresh ? "?refresh=true" : ""}`);
      setAsset(data);
      return data;
    },
    [chapterId, kind],
  );

  useEffect(() => {
    if (!active) return;
    load().catch((e) => toast.error(errMsg(e)));
  }, [active, load]);

  const pending = asset && asset.status !== "ready";

  useEffect(() => {
    if (!active || !pending) return;
    const id = setInterval(() => load(), 8000);
    return () => clearInterval(id);
  }, [active, pending, load]);

  return { asset, refresh: () => load(true) };
};

const AssetShell = ({ asset, refresh, label, note, children }) => {
  if (!asset || asset.status !== "ready") return <Loader label={label} note={note} />;
  return (
    <div>
      <button onClick={refresh} data-testid="asset-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900">
        <RefreshCw className="w-3.5 h-3.5" /> Refresh
      </button>
      <div className="mt-4">{children}</div>
    </div>
  );
};

const Resources = ({ chapterId, active }) => {
  const { asset, refresh } = useAsset(chapterId, "resources", active);
  const p = asset?.payload || {};
  return (
    <AssetShell asset={asset} refresh={refresh} label="Research agent is curating books, videos and papers…" note="Only real, retrieved sources make the list.">
      <div className="grid md:grid-cols-2 gap-4" data-testid="chapter-resources">
        <div className="card-flat p-6">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-amber-700" />
            <p className="rule-label">Books</p>
          </div>
          <ul className="mt-4 space-y-4">
            {(p.books || []).map((b) => (
              <li key={b.url} className="text-sm">
                <a href={b.url} target="_blank" rel="noreferrer" className="font-semibold hover:text-blue-700 inline-flex items-start gap-1">
                  {b.title} <ExternalLink className="w-3 h-3 mt-1 shrink-0" />
                </a>
                <p className="text-xs text-slate-500 mt-0.5">{[b.authors, b.year].filter(Boolean).join(" · ")}</p>
                {b.why && <p className="text-xs text-slate-500 italic mt-1">{b.why}</p>}
              </li>
            ))}
            {!(p.books || []).length && <p className="text-sm text-slate-500">Nothing suitable found.</p>}
          </ul>
        </div>

        <div className="card-flat p-6">
          <div className="flex items-center gap-2">
            <Youtube className="w-4 h-4 text-rose-600" />
            <p className="rule-label">Videos</p>
          </div>
          <ul className="mt-4 space-y-3">
            {(p.videos || []).map((v) => (
              <li key={v.url} className="text-sm">
                <a href={v.url} target="_blank" rel="noreferrer" data-testid="chapter-video-link" className="font-semibold hover:text-blue-700 inline-flex items-start gap-1">
                  {v.query} <ExternalLink className="w-3 h-3 mt-1 shrink-0" />
                </a>
                {v.why && <p className="text-xs text-slate-500 italic mt-0.5">{v.why}</p>}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-slate-400">Opens on YouTube in a new tab.</p>
        </div>

        <div className="card-flat p-6 md:col-span-2">
          <p className="rule-label">Research papers</p>
          <ul className="mt-4 space-y-4">
            {(p.papers || []).map((r) => (
              <li key={r.url} className="text-sm">
                <a href={r.url} target="_blank" rel="noreferrer" className="font-semibold hover:text-blue-700 inline-flex items-start gap-1">
                  {r.title} <ExternalLink className="w-3 h-3 mt-1 shrink-0" />
                </a>
                <p className="text-xs text-slate-500 mt-0.5">{[r.authors, r.year].filter(Boolean).join(" · ")}</p>
                {r.why && <p className="text-xs text-slate-500 italic mt-1">{r.why}</p>}
              </li>
            ))}
            {!(p.papers || []).length && <p className="text-sm text-slate-500">No papers at the right level for this chapter yet.</p>}
          </ul>
        </div>
      </div>
    </AssetShell>
  );
};

const RealWorld = ({ chapterId, active }) => {
  const { asset, refresh } = useAsset(chapterId, "real_world", active);
  const p = asset?.payload || {};
  return (
    <AssetShell asset={asset} refresh={refresh} label="Real-World Application agent is at work…" note="Where this chapter shows up in life, plus projects you can actually build.">
      <div className="space-y-4" data-testid="chapter-real-world">
        <div className="grid md:grid-cols-2 gap-4">
          {(p.applications || []).map((a) => (
            <div key={a.title} className="card-flat p-6">
              <h4 className="font-display font-semibold">{a.title}</h4>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.description}</p>
              {a.spotted_in && <p className="mt-3 text-xs text-slate-500 italic">Spot it: {a.spotted_in}</p>}
            </div>
          ))}
        </div>
        {(p.projects || []).map((pr) => (
          <div key={pr.title} className="card-flat p-6 bg-slate-900 text-white border-slate-800">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-amber-400" />
              <p className="rule-label !text-slate-400">Project</p>
            </div>
            <h4 className="mt-3 font-display text-lg font-semibold">{pr.title}</h4>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed">{pr.description}</p>
            <ol className="mt-4 space-y-1.5 text-sm text-slate-300 list-decimal ml-5">
              {(pr.steps || []).map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
            <div className="mt-4 flex flex-wrap gap-2">
              {(pr.materials || []).map((m) => (
                <span key={m} className="px-2.5 py-1 rounded-full bg-white/10 text-xs">
                  {m}
                </span>
              ))}
            </div>
            {pr.topics_used?.length > 0 && <p className="mt-4 text-xs text-slate-400">Uses: {pr.topics_used.join(" · ")}</p>}
          </div>
        ))}
      </div>
    </AssetShell>
  );
};

const News = ({ chapterId, active }) => {
  const { asset, refresh } = useAsset(chapterId, "news", active);
  const items = asset?.payload?.news || [];
  return (
    <AssetShell asset={asset} refresh={refresh} label="Fetching recent, age-appropriate news…" note="Live headlines, filtered by the Research agent.">
      <div className="space-y-3" data-testid="chapter-news">
        {items.map((n) => (
          <a key={n.url} href={n.url} target="_blank" rel="noreferrer" className="block card-flat p-5 hover:!border-slate-900">
            <div className="flex items-center gap-2">
              <Newspaper className="w-3.5 h-3.5 text-teal-700" />
              <span className="rule-label">{n.source || "news"}</span>
              {n.published && <span className="text-xs text-slate-400">{n.published}</span>}
            </div>
            <p className="mt-2 font-semibold text-sm">{n.title}</p>
            {n.why && <p className="mt-1 text-sm text-slate-500">{n.why}</p>}
          </a>
        ))}
        {!items.length && <Empty title="No stories worth your time right now" note="Try refreshing later — nothing matched this chapter safely." />}
      </div>
    </AssetShell>
  );
};

export default function ChapterPage() {
  const { chapterId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "topics";
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(`/chapters/${chapterId}`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        toast.error(errMsg(err));
        navigate("/library");
      });
  }, [chapterId, navigate]);

  if (!data) return <Shell><Loader label="Opening chapter…" /></Shell>;

  const { chapter, topics, progress } = data;

  return (
    <Shell>
      <div className="reveal" data-testid="chapter-page">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-slate-500">
          <Link to="/library" className="hover:text-slate-900">
            Library
          </Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span>{chapter.subject_name}</span>
        </nav>

        <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold leading-tight">{chapter.name}</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">{chapter.summary}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Pill tone="blue">{chapter.est_hours} h</Pill>
          <Pill tone="slate">
            {progress.completed}/{progress.total} topics · {progress.percent}%
          </Pill>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`chapter-tab-${key}`}
              onClick={() => setParams({ tab: key })}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "topics" && (
            <ul className="space-y-3" data-testid="chapter-topics">
              {topics.map((t, i) => (
                <li key={t.topic_id} className="card-flat p-5 flex flex-wrap items-center justify-between gap-4">
                  <div className="max-w-xl">
                    <p className="text-sm font-semibold">
                      <span className="font-mono-x text-xs text-slate-400 mr-2">{String(i + 1).padStart(2, "0")}</span>
                      {t.title}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">{t.summary}</p>
                    {t.best_score ? <p className="mt-1 text-xs text-slate-400">best {t.best_score}%</p> : null}
                  </div>
                  <span className="flex items-center gap-3 shrink-0">
                    <Pill tone={statusTone[t.status]}>{t.status.replace("_", " ")}</Pill>
                    <button
                      data-testid={`chapter-open-topic-${t.topic_id}`}
                      onClick={() => navigate(`/topic/${t.topic_id}`)}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                    >
                      <PlayCircle className="w-3.5 h-3.5" /> Study
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {tab === "practice" && <PracticePanel scope="chapter" id={chapterId} label="Problems mixed across every topic in this chapter." />}
          {tab === "resources" && <Resources chapterId={chapterId} active />}
          {tab === "real_world" && <RealWorld chapterId={chapterId} active />}
          {tab === "news" && <News chapterId={chapterId} active />}
        </div>
      </div>
    </Shell>
  );
}
